import { PHASE_WARNING_TICKS, TICK_HZ, toMove, type Outcome, type PlayerId, type State, type Winner } from '@chessshooter/shared';

// The HUD's words for the match as pure functions of the state, tested without a DOM.

export interface StatusLine {
  text: string;
  /** colours the line: the own turn, the opponent's, or the countdown into the next phase */
  tone: 'mine' | 'foe' | 'warn';
}

/** Whole seconds until the phase ends, once the countdown is due; null before that and after the match. */
export function phaseCountdown(state: State): number | null {
  if (state.winner !== null || state.transitionTicks > 0) return null;
  const remaining = state.phaseEndsAtTick - state.tick;
  if (remaining > PHASE_WARNING_TICKS) return null;
  return Math.max(1, Math.ceil(remaining / TICK_HZ));
}

/** The line above the board (whose move it is) or under the HUD bar (the countdown); null when there is nothing to say. */
export function statusLine(state: State, me: PlayerId, foe: string, check: boolean): StatusLine | null {
  if (state.winner !== null || state.transitionTicks > 0) return null;
  const countdown = phaseCountdown(state);
  if (countdown !== null) return { text: `${state.phase === 'chess' ? 'Arena' : 'Schach'} in ${countdown} …`, tone: 'warn' };
  if (state.phase !== 'chess') return null;
  const mine = toMove(state) === me;
  const text = mine ? 'Du bist am Zug' : `${foe} zieht`;
  return { text: check ? `${text} · Schach!` : text, tone: mine ? 'mine' : 'foe' };
}

/** How the match ended, for the line under the verdict. */
export function resultDetail(outcome: Outcome | null, winner: Winner | null, me: PlayerId): string | null {
  switch (outcome) {
    case 'checkmate':
      return 'Schachmatt';
    case 'stalemate':
      return 'Patt';
    case 'material':
      return 'Zu wenig Material für ein Matt';
    case 'clock':
      if (winner === 'draw') return 'Beide Uhren sind abgelaufen';
      return winner === me ? 'Die gegnerische Uhr ist abgelaufen' : 'Deine Uhr ist abgelaufen';
    default:
      return null;
  }
}

export function transitionText(state: State, me: PlayerId): string[] | null {
  if (state.winner !== null || state.transitionTicks === 0) return null;
  return [state.phase === 'chess' ? 'Schach' : 'Arena',
    ...(state.phase === 'chess' ? [toMove(state) === me ? 'Du bist am Zug' : 'Gegner ist am Zug'] : []),
    `${Math.ceil(state.transitionTicks / TICK_HZ)}`];
}
